/**
 * Marketing integration-status matrix — the single source of truth for which
 * integrations exist and how mature each one is on the public site.
 *
 * Every integration mentioned anywhere in marketing copy or public docs must
 * have an entry here (guard: `npm run check:integration-matrix`), and its
 * status must match reality:
 *
 *   live        — implemented end to end, tested, in the Connected Systems
 *                 picker; safe to describe as shipped.
 *   configured  — implemented, but active only where a tenant sets it up
 *                 (JSM customer replies, Argo gates, database connector).
 *   preview     — early/partial; must be labelled preview wherever mentioned.
 *   planned     — roadmap only. Copy mentioning these MUST carry roadmap /
 *                 not-yet wording (the guard enforces it).
 *
 * `matchNames` are the lowercase tokens the coverage guard treats as a
 * mention of this entry. Display copy lives in the `integrationsMatrix` i18n
 * namespace (src/i18n/locales/marketing/integrations-matrix.ts, EN + NL);
 * `name` stays here untranslated (product names are not translated).
 *
 * DISPLAY DATA ONLY — never imported by auth, routing or governance code.
 * The runtime truth registry is src/server/integrations/
 * outbound-provider-capabilities.ts; when promoting a status here, check that
 * registry (and docs/POSITIONING.md §2) first.
 */

export type IntegrationStatus = "live" | "configured" | "preview" | "planned";

export type IntegrationMatrixEntry = {
  /** i18n key under `integrationsMatrix.items`. */
  id: string;
  /** Untranslated display name. */
  name: string;
  status: IntegrationStatus;
  /** Marketing deep page, when one exists. */
  href?: string;
  /** Lowercase tokens the coverage guard counts as a mention of this entry. */
  matchNames: string[];
};

export const INTEGRATION_MATRIX: readonly IntegrationMatrixEntry[] = [
  { id: "slack", name: "Slack", status: "live", href: "/integrations/slack", matchNames: ["slack"] },
  { id: "teams", name: "Microsoft Teams", status: "live", href: "/integrations/teams", matchNames: ["microsoft teams", "teams"] },
  { id: "freshdesk", name: "Freshdesk", status: "live", href: "/integrations/freshdesk", matchNames: ["freshdesk"] },
  { id: "jira", name: "Jira Software", status: "live", href: "/integrations/jira", matchNames: ["jira software", "jira"] },
  { id: "jsm", name: "Jira Service Management", status: "configured", href: "/integrations/jira", matchNames: ["jira service management", "jsm"] },
  { id: "odoo", name: "Odoo Helpdesk", status: "live", href: "/integrations/odoo", matchNames: ["odoo helpdesk", "odoo"] },
  { id: "topdesk", name: "TOPdesk", status: "live", href: "/integrations/topdesk", matchNames: ["topdesk"] },
  // HR connectors — read-only, and "configured" rather than "live" on purpose:
  // both need per-organization credentials plus an explicit per-project
  // activation that is set up during onboarding (there is no self-service
  // connection form yet). See src/server/hr/{afas,youforce}-connector.ts.
  { id: "afas", name: "AFAS Profit (HR)", status: "configured", href: "/integrations/afas", matchNames: ["afas profit", "afas"] },
  { id: "youforce", name: "Visma Raet Youforce (HR)", status: "configured", href: "/integrations/youforce", matchNames: ["visma raet youforce", "youforce", "visma raet"] },
  { id: "github", name: "GitHub", status: "live", href: "/integrations/github", matchNames: ["github"] },
  { id: "gitlab", name: "GitLab", status: "live", href: "/integrations/gitlab", matchNames: ["gitlab"] },
  { id: "bitbucket", name: "Bitbucket (Cloud & Server)", status: "live", matchNames: ["bitbucket"] },
  { id: "linear", name: "Linear", status: "live", matchNames: ["linear"] },
  { id: "email", name: "Email — Gmail & Office 365", status: "live", matchNames: ["gmail", "office 365"] },
  { id: "websiteChat", name: "Website chat (public widget)", status: "live", matchNames: ["website chat", "chat widget", "websitechat"] },
  { id: "kubernetesArgo", name: "Kubernetes / Argo Rollouts", status: "configured", href: "/integrations/argo", matchNames: ["kubernetes", "argo"] },
  // The FC Agent is FixControl's own connectivity component, not a
  // third-party provider — listed so the private-infrastructure story is
  // discoverable from the matrix. "configured": it runs only where an
  // organization enrols and installs it (pilot onboarding).
  { id: "fcAgent", name: "FC Agent (private infrastructure)", status: "configured", href: "/integrations/fc-agent", matchNames: ["fc agent", "fc-agent"] },
  // CI/CD deployment gates: a pause the pipeline is already holding opens a
  // FixControl approval; the verdict executes through the host's own
  // primitive (directly or via the FC Agent). GitLab + Jenkins were
  // live-tested end to end (docs/golden-path-cicd-agent.md); GitHub Actions
  // is built with the prod webhook e2e pending an operator-side App change.
  // "configured": per-tenant opt-in, defaults off, enabled during pilots.
  {
    id: "cicdGates",
    name: "CI/CD deployment gates — GitHub Actions, GitLab CI, Jenkins",
    status: "configured",
    href: "/integrations/fc-agent",
    matchNames: ["github actions", "gitlab ci", "jenkins"],
  },
  { id: "database", name: "Database connector (PostgreSQL)", status: "configured", matchNames: ["database connector", "databaseconnector", "postgresql"] },
  { id: "gcp", name: "Google Cloud (read-only diagnostics)", status: "configured", matchNames: ["google cloud", "cloud logging", "error reporting", "bigquery"] },
  { id: "api", name: "Public API & webhooks", status: "live", matchNames: ["public api", "openapi"] },
  {
    id: "devops",
    name: "CI/CD event ingest (observe-and-govern)",
    status: "preview",
    // "argo cd"/"argo" belong to the kubernetesArgo entry and GitHub
    // Actions / GitLab CI / Jenkins to the cicdGates entry (deployment
    // gates); this entry covers the remaining observe-only ingest sources.
    matchNames: ["azure devops", "vercel", "terraform cloud"],
  },
  { id: "whatsapp", name: "WhatsApp", status: "planned", matchNames: ["whatsapp"] },
  { id: "saml", name: "SAML SSO", status: "planned", matchNames: ["saml"] },
];
